/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroup
} from 'react-bootstrap';
import mapboxgl from 'mapbox-gl';
import Dexie from 'dexie';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';

// Constants
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
const OLA_API_KEY = process.env.REACT_APP_OLA_API_KEY;
const BATCH_SIZE = 10;
const PADDING_FACTOR = 0.2;
const AVERAGE_TILE_SIZE_KB = 25;
const DEFAULT_LOCATION = { lat: 28.6139, lng: 77.209, zoom: 12 };

// Initialize DexieDB for tile storage
const db = new Dexie('mapTilesDB');
db.version(1).stores({
  tiles: 'url, data, timestamp'
});

// Utility Functions
const pointToTile = (lon, lat, z) => {
  const scale = Math.pow(2, z);
  const rad = Math.PI / 180;
  const x = Math.floor(((lon + 180) / 360) * scale);
  const y = Math.floor(
    ((1 - Math.log(Math.tan(lat * rad) + 1 / Math.cos(lat * rad)) / Math.PI) /
      2) *
      scale
  );
  return [x, y];
};

const formatFileSize = sizeKB => {
  return sizeKB > 1024
    ? `${(sizeKB / 1024).toFixed(1)} MB`
    : `${sizeKB.toFixed(0)} KB`;
};

const applyBoundsPadding = bounds => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latDiff = ne.lat - sw.lat;
  const lngDiff = ne.lng - sw.lng;

  return {
    ne: {
      lat: Math.min(ne.lat + latDiff * PADDING_FACTOR, 85),
      lng: Math.min(ne.lng + lngDiff * PADDING_FACTOR, 180)
    },
    sw: {
      lat: Math.max(sw.lat - latDiff * PADDING_FACTOR, -85),
      lng: Math.max(sw.lng - lngDiff * PADDING_FACTOR, -180)
    }
  };
};

// API Functions
const fetchAddressFromOla = async (lat, lng, apiKey) => {
  if (!apiKey) {
    console.warn('OLA API key not provided');
    return null;
  }

  try {
    const response = await axios.get(
      `https://api.ola.app/services/reverse-geocode/${lat}/${lng}`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`
        }
      }
    );

    if (response.data?.data) {
      return {
        address: response.data.data.address || '',
        city: response.data.data.city || '',
        state: response.data.data.state || '',
        country: response.data.data.country || '',
        pin: response.data.data.pin || ''
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching address from OLA:', error);
    return null;
  }
};

const searchLocations = async query => {
  try {
    const response = await axios.get(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        query
      )}.json`,
      {
        params: {
          access_token: MAPBOX_TOKEN,
          limit: 5,
          types: 'place,locality,neighborhood,address'
        }
      }
    );
    return response.data.features || [];
  } catch (error) {
    console.error('Search error:', error);
    return [];
  }
};

// Tile Management Functions
const calculateZoomBounds = currentZoom => {
  return {
    minZoom: Math.max(8, Math.floor(currentZoom - 3)),
    maxZoom: 18
  };
};

const calculateTilesByZoom = (paddedBounds, minZoom, maxZoom) => {
  const tilesByZoom = {};
  let totalTiles = 0;

  for (let z = minZoom; z <= maxZoom; z++) {
    const nwTile = pointToTile(paddedBounds.sw.lng, paddedBounds.ne.lat, z);
    const seTile = pointToTile(paddedBounds.ne.lng, paddedBounds.sw.lat, z);
    const tilesX = Math.max(1, seTile[0] - nwTile[0] + 1);
    const tilesY = Math.max(1, seTile[1] - nwTile[1] + 1);
    const zoomTiles = tilesX * tilesY;

    totalTiles += zoomTiles;
    tilesByZoom[z] = {
      count: zoomTiles,
      x: { min: nwTile[0], max: seTile[0] },
      y: { min: nwTile[1], max: seTile[1] }
    };
  }

  return { tilesByZoom, totalTiles };
};

const downloadTileBatch = async batch => {
  return Promise.all(
    batch.map(async ({ x, y, z }) => {
      const tileUrl = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/${z}/${x}/${y}?access_token=${MAPBOX_TOKEN}`;

      try {
        const existingTile = await db.tiles.get(tileUrl);
        if (existingTile) return { success: true };

        const response = await fetch(tileUrl, {
          method: 'GET',
          headers: { Accept: 'image/*' },
          cache: 'no-store'
        });

        if (!response.ok) {
          console.warn(
            `Failed to download tile: z=${z}, x=${x}, y=${y}, status=${response.status}`
          );
          return { success: false };
        }

        const buffer = await response.arrayBuffer();
        if (buffer.byteLength === 0) {
          console.warn(`Empty tile: z=${z}, x=${x}, y=${y}`);
          return { success: false };
        }

        await db.tiles.put({
          url: tileUrl,
          data: buffer,
          timestamp: Date.now()
        });

        if (z >= 17) {
          console.log(
            `Successfully saved high-zoom tile: z=${z}, x=${x}, y=${y}`
          );
        }

        return { success: true };
      } catch (err) {
        console.error(`Error downloading tile: z=${z}, x=${x}, y=${y}:`, err);
        return { success: false };
      }
    })
  );
};

// SearchBox Component
const SearchBox = ({ onSearchSelect, apiKey }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const handleSearchChange = async e => {
    const value = e.target.value;
    setQuery(value);

    if (value.length < 3) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);

    const searchResults = await searchLocations(value);
    setResults(searchResults);
    setIsSearching(false);
  };

  const handleResultClick = async result => {
    const [lng, lat] = result.center;
    let address = result.place_name;
    let addressDetails = null;

    if (apiKey) {
      try {
        addressDetails = await fetchAddressFromOla(lat, lng, apiKey);
        if (addressDetails?.address) {
          address = addressDetails.address;
        }
      } catch (error) {
        console.error('Error fetching OLA address:', error);
      }
    }

    onSearchSelect({
      lat,
      lng,
      address,
      details: addressDetails
    });

    setQuery(address);
    setShowResults(false);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="search-container position-relative mb-3" ref={searchRef}>
      <InputGroup>
        <Form.Control
          type="text"
          placeholder="Search for a location..."
          value={query}
          onChange={handleSearchChange}
          onFocus={() => {
            if (results.length > 0) setShowResults(true);
          }}
        />
        {isSearching && (
          <InputGroup.Text>
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {showResults && results.length > 0 && (
        <div
          className="search-results position-absolute w-100 bg-white border rounded shadow-sm"
          style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}
        >
          {results.map(result => (
            <div
              key={result.id}
              className="p-2 border-bottom cursor-pointer hover-bg-light"
              onClick={() => handleResultClick(result)}
              style={{ cursor: 'pointer' }}
              onMouseOver={e => (e.target.style.backgroundColor = '#f0f0f0')}
              onMouseOut={e => (e.target.style.backgroundColor = 'white')}
            >
              <div className="fw-bold">{result.text}</div>
              <div className="small text-muted">{result.place_name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// MapClickHandler Component
const MapClickHandler = ({
  setSelectedLocation,
  setAddressDetails,
  apiKey
}) => {
  const map = useRef(null);
  const marker = useRef(null);

  useEffect(() => {
    const mapInstance = document.querySelector('.mapboxgl-map');
    if (mapInstance) {
      map.current = mapInstance.__mapboxgl;
    }
  }, []);

  useEffect(() => {
    if (!map.current) return;

    const handleClick = async e => {
      const { lat, lng } = e.lngLat;
      const locationData = { lat, lng };

      // Update marker
      if (!marker.current) {
        marker.current = new mapboxgl.Marker({ color: '#E03131' })
          .setLngLat([lng, lat])
          .addTo(map.current);
      } else {
        marker.current.setLngLat([lng, lat]);
      }

      // Fetch address if online and API key available
      if (navigator.onLine && apiKey) {
        try {
          const details = await fetchAddressFromOla(lat, lng, apiKey);
          if (details) {
            locationData.address = details.address || '';
            setAddressDetails(details);
          }
        } catch (error) {
          console.error('Error fetching address:', error);
          locationData.address = '';
        }
      } else {
        locationData.address = '';
      }

      setSelectedLocation(locationData);
    };

    map.current.on('click', handleClick);
    return () => {
      if (map.current) {
        map.current.off('click', handleClick);
      }
    };
  }, [apiKey, setSelectedLocation, setAddressDetails]);

  return null;
};

// Main OfflineMap Component
const OfflineMap = ({ show, onHide }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const boundsRef = useRef(null);

  // Map state
  const [lng, setLng] = useState(DEFAULT_LOCATION.lng);
  const [lat, setLat] = useState(DEFAULT_LOCATION.lat);
  const [zoom, setZoom] = useState(DEFAULT_LOCATION.zoom);

  // Download state
  const [downloadStatus, setDownloadStatus] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [estimatedSize, setEstimatedSize] = useState(null);
  const [mapDownloadComplete, setMapDownloadComplete] = useState(false);

  // Location state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);

  // Calculate estimated download size
  const calculateEstimatedSize = () => {
    if (!map.current) return;

    const bounds = boundsRef.current || map.current.getBounds();
    const currentZoom = map.current.getZoom();
    const { minZoom, maxZoom } = calculateZoomBounds(currentZoom);
    const paddedBounds = applyBoundsPadding(bounds);
    const { totalTiles } = calculateTilesByZoom(paddedBounds, minZoom, maxZoom);

    const estimatedSizeKB = totalTiles * AVERAGE_TILE_SIZE_KB;
    const size = formatFileSize(estimatedSizeKB);

    setEstimatedSize({ tiles: totalTiles, size });
  };

  // Initialize map
  useEffect(() => {
    if (!show || map.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [lng, lat], // Use current state values
      zoom: zoom,
      maxZoom: 18
    });

    map.current.addControl(new mapboxgl.NavigationControl());

    map.current.on('move', () => {
      setLng(map.current.getCenter().lng.toFixed(4));
      setLat(map.current.getCenter().lat.toFixed(4));
      setZoom(map.current.getZoom().toFixed(2));
    });

    map.current.on('load', () => {
      boundsRef.current = map.current.getBounds();
      calculateEstimatedSize();
    });

    map.current.on('moveend', () => {
      boundsRef.current = map.current.getBounds();
      calculateEstimatedSize();
    });

    return () => {
      if (map.current && !show) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [show, lng, lat, zoom]);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setMapDownloadComplete(false);
      setDownloadStatus('');
      setDownloadProgress(0);
    }
  }, [show]);

  // On modal open, check geolocation permission and set location
  useEffect(() => {
    if (!show) return;
    let didCancel = false;

    const setToUserLocation = position => {
      if (didCancel) return;

      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;
      const userZoom = 14;

      // Update state
      setLat(userLat);
      setLng(userLng);
      setZoom(userZoom);

      // If map is already initialized, fly to user location
      if (map.current) {
        map.current.flyTo({
          center: [userLng, userLat],
          zoom: userZoom,
          essential: true
        });

        // Update bounds reference
        setTimeout(() => {
          if (map.current && !didCancel) {
            boundsRef.current = map.current.getBounds();
            calculateEstimatedSize();
          }
        }, 1000); // Wait for flyTo animation to complete
      }
    };

    const handleLocationError = error => {
      console.log('Geolocation error:', error);
      // Silently fail - keep default location
    };

    const handlePermission = result => {
      if (didCancel) return;

      if (result.state === 'granted') {
        navigator.geolocation.getCurrentPosition(
          setToUserLocation,
          handleLocationError,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      } else if (result.state === 'prompt') {
        navigator.geolocation.getCurrentPosition(
          setToUserLocation,
          handleLocationError,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      }
      // If denied, do nothing (keep default)
    };

    if (navigator.permissions && navigator.geolocation) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(handlePermission)
        .catch(() => {
          // fallback: try to get location directly
          if (!didCancel) {
            navigator.geolocation.getCurrentPosition(
              setToUserLocation,
              handleLocationError,
              {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000
              }
            );
          }
        });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        setToUserLocation,
        handleLocationError,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    }

    return () => {
      didCancel = true;
    };
  }, [show]);

  // Handle location selection
  const handleLocationSelect = location => {
    setSelectedLocation(location);

    if (!map.current) return;

    map.current.flyTo({
      center: [location.lng, location.lat],
      zoom: 16,
      essential: true
    });

    if (map.current._locationMarker) {
      map.current._locationMarker.remove();
    }

    const marker = new mapboxgl.Marker({ color: '#E03131' })
      .setLngLat([location.lng, location.lat])
      .addTo(map.current);

    map.current._locationMarker = marker;
  };

  // Download tiles for a specific zoom level
  const downloadZoomLevel = async (
    z,
    tileInfo,
    setProgress,
    totalTiles,
    downloadedTilesRef,
    failedTilesRef
  ) => {
    const {
      x: { min: minX, max: maxX },
      y: { min: minY, max: maxY }
    } = tileInfo;
    const tilesToDownload = [];

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        tilesToDownload.push({ x, y, z });
      }
    }

    for (let i = 0; i < tilesToDownload.length; i += BATCH_SIZE) {
      const batch = tilesToDownload.slice(i, i + BATCH_SIZE);
      const results = await downloadTileBatch(batch);

      results.forEach(result => {
        downloadedTilesRef.current++;
        if (!result.success) {
          failedTilesRef.current++;
        }

        const progress = Math.round(
          (downloadedTilesRef.current / totalTiles) * 100
        );
        setProgress(progress);
      });

      setDownloadStatus(
        `Downloaded ${
          downloadedTilesRef.current
        }/${totalTiles} tiles (${Math.round(
          (downloadedTilesRef.current / totalTiles) * 100
        )}%)`
      );
    }
  };

  // Main download function
  const performDownload = async () => {
    setIsDownloading(true);
    setMapDownloadComplete(false);
    setDownloadStatus('Starting download...');
    setDownloadProgress(0);

    try {
      await db.tiles.clear();

      const bounds = boundsRef.current || map.current.getBounds();
      const currentZoom = map.current.getZoom();
      const { minZoom, maxZoom } = calculateZoomBounds(currentZoom);
      const paddedBounds = applyBoundsPadding(bounds);
      const { tilesByZoom, totalTiles } = calculateTilesByZoom(
        paddedBounds,
        minZoom,
        maxZoom
      );

      console.log(
        `Downloading area: SW(${paddedBounds.sw.lat}, ${paddedBounds.sw.lng}) to NE(${paddedBounds.ne.lat}, ${paddedBounds.ne.lng})`
      );
      console.log(`Zoom levels: ${minZoom} to ${maxZoom}`);

      setDownloadStatus(
        `Downloading ${totalTiles} tiles across all zoom levels...`
      );

      const downloadedTilesRef = { current: 0 };
      const failedTilesRef = { current: 0 };

      for (let z = minZoom; z <= maxZoom; z++) {
        console.log(`Downloading zoom level ${z} tiles...`);
        setDownloadStatus(
          `Downloading zoom level ${z} tiles (${z - minZoom + 1}/${
            maxZoom - minZoom + 1
          })...`
        );

        await downloadZoomLevel(
          z,
          tilesByZoom[z],
          setDownloadProgress,
          totalTiles,
          downloadedTilesRef,
          failedTilesRef
        );
      }

      // Verify downloaded tiles
      try {
        const allTiles = await db.tiles.toArray();
        const zoomLevelCounts = {};

        allTiles.forEach(tile => {
          const matches = tile.url.match(/\/(\d+)\/(\d+)\/(\d+)\?/);
          if (matches) {
            const z = parseInt(matches[1]);
            zoomLevelCounts[z] = (zoomLevelCounts[z] || 0) + 1;
          }
        });

        console.log('Downloaded tiles by zoom level:', zoomLevelCounts);
      } catch (err) {
        console.error('Error counting tiles by zoom level:', err);
      }

      const finalMessage =
        failedTilesRef.current > 0
          ? `Download complete! ${
              downloadedTilesRef.current - failedTilesRef.current
            } tiles saved successfully. ${
              failedTilesRef.current
            } tiles couldn't be downloaded.`
          : 'Map download complete! Your maps are ready for offline use.';

      setDownloadStatus(finalMessage);
      setMapDownloadComplete(true);
    } catch (err) {
      console.error('Error downloading map tiles:', err);
      setDownloadStatus('Error downloading tiles. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadMapTiles = async () => {
    if (!map.current || isDownloading) return;
    await performDownload();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="map-download-modal"
      backdrop="static"
      keyboard={!isDownloading}
    >
      <Modal.Header closeButton={!isDownloading}>
        <Modal.Title>Download Map for Offline Use</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="search-controls mb-3">
          <SearchBox
            onSearchSelect={handleLocationSelect}
            apiKey={OLA_API_KEY}
          />
          <div className="d-flex justify-content-between align-items-center mt-2">
            {estimatedSize && (
              <div className="text-muted small">
                Estimated download: ~{estimatedSize.size} ({estimatedSize.tiles}{' '}
                tiles)
              </div>
            )}
          </div>
        </div>

        <div className="map-container-wrapper">
          <div
            ref={mapContainer}
            className="map-modal-container"
            style={{
              height: '50vh',
              width: '100%',
              borderRadius: '4px',
              marginBottom: '10px'
            }}
          />
          <div className="map-instructions">
            <p className="mb-2">
              Search for a location, use current location, or pan and zoom to
              select the area you want to download.
            </p>
            <p className="mb-0 text-muted small">
              All zoom levels from 8 up to high-resolution (level 18) will be
              downloaded to ensure clarity at all zoom levels.
            </p>
          </div>
        </div>

        <MapClickHandler
          setSelectedLocation={setSelectedLocation}
          setAddressDetails={setAddressDetails}
          apiKey={OLA_API_KEY}
        />

        {downloadStatus && (
          <div className="download-status mt-3">
            <p className="mb-1 font-weight-bold">{downloadStatus}</p>
            {isDownloading && (
              <div className="progress" style={{ height: '10px' }}>
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${downloadProgress}%` }}
                  aria-valuenow={downloadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            )}
          </div>
        )}

        {mapDownloadComplete && !isDownloading && (
          <Alert variant="success" className="mt-3">
            <Alert.Heading>Map Download Complete!</Alert.Heading>
            <p>
              Map tiles have been downloaded successfully and are now available
              for offline use.
            </p>
          </Alert>
        )}

        {selectedLocation?.address && (
          <div className="selected-location mt-3">
            <small className="text-muted">Selected Location:</small>
            <p className="mb-0 fw-bold">{selectedLocation.address}</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!isDownloading ? (
          <>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
            {!mapDownloadComplete && (
              <Button variant="primary" onClick={downloadMapTiles}>
                Download Current Map Area
              </Button>
            )}
          </>
        ) : (
          <Button variant="secondary" disabled>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Downloading...
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default OfflineMap;
